import type { SupabaseClient } from '@supabase/supabase-js';
import {
  designationPinJson,
  type StoredDesignationPin,
} from './associationDesignationPins';
import {
  APPLY_FAILURE_MESSAGE,
  type ApplyPolicy,
  type DesignationDefaults,
  type IdentifierRef,
} from './organisationDefaults';
import { RESET_BOTH_FAILURE } from './rowOverrides';

export interface DesignationRpcResult {
  ok: boolean;
  conflict?: boolean;
  count?: number;
  role_label?: string;
  established?: boolean;
  reassigned?: boolean;
  column_key?: string;
  identifier_type?: string;
  code?: string;
  message?: string;
  correlation_id?: string;
}

type RpcClient = Pick<SupabaseClient, 'rpc'>;

function failure(message = APPLY_FAILURE_MESSAGE): DesignationRpcResult {
  return { ok: false, message, code: 'apply_failed' };
}

async function callRpc(
  client: RpcClient,
  fn: string,
  args: Record<string, unknown>,
): Promise<DesignationRpcResult> {
  if (typeof client.rpc !== 'function') return failure();
  try {
    const { data, error } = await client.rpc(fn as never, args as never);
    if (error) return failure(APPLY_FAILURE_MESSAGE);
    if (!data || typeof data !== 'object') return failure();
    return data as DesignationRpcResult;
  } catch {
    return failure();
  }
}

export async function applyOrganisationDefaults(
  client: RpcClient,
  input: {
    organisationId: string;
    soldAs: IdentifierRef | null;
    boughtAs: IdentifierRef | null;
    policy?: ApplyPolicy | null;
    actor?: string;
  },
): Promise<DesignationRpcResult> {
  return callRpc(client, 'apply_organisation_designation_defaults', {
    p_org: input.organisationId,
    p_sold_as_column_key: input.soldAs?.columnKey ?? null,
    p_sold_as_identifier_type: input.soldAs?.identifierType ?? null,
    p_bought_as_column_key: input.boughtAs?.columnKey ?? null,
    p_bought_as_identifier_type: input.boughtAs?.identifierType ?? null,
    p_policy: input.policy ?? null,
    p_actor: input.actor ?? null,
  });
}

export async function establishOrganisationDefaults(
  client: RpcClient,
  organisationId: string,
): Promise<DesignationRpcResult> {
  return callRpc(client, 'establish_organisation_designation_defaults', { p_org: organisationId });
}

export async function setSoldAsOverride(
  client: RpcClient,
  input: {
    organisationId: string;
    masterProductId: string;
    columnKey: string | null;
    identifierType: string | null;
    action: 'override' | 'reset';
  },
): Promise<DesignationRpcResult> {
  return callRpc(client, 'set_variant_sold_as_override', {
    p_org: input.organisationId,
    p_master_product_id: input.masterProductId,
    p_column_key: input.columnKey,
    p_identifier_type: input.identifierType,
    p_action: input.action,
    p_actor: null,
  });
}

export async function setBoughtAsOverride(
  client: RpcClient,
  input: {
    organisationId: string;
    masterProductId: string;
    columnKey: string | null;
    identifierType: string | null;
    action: 'override' | 'reset';
  },
): Promise<DesignationRpcResult> {
  return callRpc(client, 'set_variant_bought_as_override', {
    p_org: input.organisationId,
    p_master_product_id: input.masterProductId,
    p_column_key: input.columnKey,
    p_identifier_type: input.identifierType,
    p_action: input.action,
    p_actor: null,
  });
}

export async function applyVariantDesignationPins(
  client: RpcClient,
  input: {
    organisationId: string;
    rows: Array<{
      masterProductId: string;
      soldAs: StoredDesignationPin;
      boughtAs: StoredDesignationPin;
    }>;
  },
): Promise<DesignationRpcResult> {
  return callRpc(client, 'apply_variant_designation_pins', {
    p_org: input.organisationId,
    p_rows: input.rows.map((row) => ({
      master_product_id: row.masterProductId,
      sold_as: designationPinJson(row.soldAs),
      bought_as: designationPinJson(row.boughtAs),
    })),
    p_actor: null,
  });
}

export async function resetVariantDesignations(
  client: RpcClient,
  input: { organisationId: string; masterProductId: string },
): Promise<DesignationRpcResult> {
  const result = await callRpc(client, 'reset_variant_designation_overrides', {
    p_org: input.organisationId,
    p_master_product_id: input.masterProductId,
    p_actor: null,
  });
  if (!result.ok) return { ...result, message: RESET_BOTH_FAILURE };
  return result;
}

export async function reassignRemovedDefaultColumn(
  client: RpcClient,
  input: {
    organisationId: string;
    removedColumnKey: string;
    replacement: IdentifierRef;
    actor?: string;
  },
): Promise<DesignationRpcResult> {
  return callRpc(client, 'reassign_removed_designation_column', {
    p_org: input.organisationId,
    p_removed_column_key: input.removedColumnKey,
    p_replacement_column_key: input.replacement.columnKey,
    p_replacement_identifier_type: input.replacement.identifierType,
    p_actor: input.actor ?? null,
  });
}

export function defaultsFromRow(row: {
  sold_as_column_key: string | null;
  sold_as_identifier_type: string | null;
  bought_as_column_key: string | null;
  bought_as_identifier_type: string | null;
} | null): DesignationDefaults {
  if (!row) return { soldAs: null, boughtAs: null };
  return {
    soldAs:
      row.sold_as_column_key && row.sold_as_identifier_type
        ? { columnKey: row.sold_as_column_key, identifierType: row.sold_as_identifier_type }
        : null,
    boughtAs:
      row.bought_as_column_key && row.bought_as_identifier_type
        ? { columnKey: row.bought_as_column_key, identifierType: row.bought_as_identifier_type }
        : null,
  };
}
